import { Box, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PAGE_CHANGE_KINDS, type PageChangeRow } from 'shared';
import { formatDateTime } from '../format';
import { MONO, tokens } from '../theme';

/**
 * Danh sách thay đổi giữa hai lần quét — spec SC-19.
 *
 * Dùng ở hai chỗ với cùng một hình thức: dải cảnh báo trên màn chi tiết một trang,
 * và dòng thời gian của mọi trang ở màn `/ra-soat`. Khác nhau đúng một điều — dòng
 * thời gian có thêm tên trang ở đầu mỗi dòng.
 */

/** Giá trị trước → sau. Đây là phần người dùng thật sự đọc, không phải cái nhãn. */
function TruocSau({ before, after }: { before: string | null; after: string | null }) {
  if (before === null && after === null) return null;
  return (
    <Stack
      direction="row"
      spacing={0.8}
      alignItems="center"
      flexWrap="wrap"
      sx={{ mt: 0.4, fontFamily: MONO, fontSize: 12 }}
    >
      <Box
        component="span"
        sx={{
          color: tokens.muted,
          // Gạch ngang giá trị cũ: nhìn một cái là biết vế nào không còn nữa, không
          // phải đọc mũi tên rồi mới hiểu.
          textDecoration: 'line-through',
          maxWidth: 340,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {before ?? '(trống)'}
      </Box>
      <Box component="span" sx={{ color: tokens.faint }}>
        →
      </Box>
      <Box
        component="span"
        sx={{
          color: tokens.text,
          fontWeight: 500,
          maxWidth: 340,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {after ?? '(trống)'}
      </Box>
    </Stack>
  );
}

export function ChangeList({
  changes,
  showPage = false,
}: {
  changes: readonly PageChangeRow[];
  /** Hiện tên trang ở đầu mỗi dòng — chỉ cần ở dòng thời gian chung */
  showPage?: boolean;
}) {
  return (
    <Stack spacing={0}>
      {changes.map((c) => {
        const meta = PAGE_CHANGE_KINDS[c.kind];
        const nang = c.severity === 'nghiem-trong';
        return (
          <Stack
            key={c.id}
            direction="row"
            spacing={1.5}
            sx={{
              py: 1.2,
              px: 1.5,
              borderLeft: `3px solid ${nang ? tokens.rose : tokens.line}`,
              bgcolor: nang ? tokens.roseBg : 'transparent',
              borderBottom: `1px solid ${tokens.line2}`,
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                {showPage && c.pageSlug && (
                  <Typography
                    component={RouterLink}
                    to={`/pages/${c.pageSlug}`}
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: tokens.teal,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {c.pageCode}
                  </Typography>
                )}
                <Typography
                  sx={{ fontSize: 13.5, fontWeight: nang ? 600 : 400, color: nang ? tokens.roseText : tokens.text }}
                >
                  {meta.label}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 12.5, color: tokens.muted, mt: 0.2 }}>
                {meta.giaiThich}
              </Typography>
              <TruocSau before={c.before} after={c.after} />
            </Box>
            <Typography sx={{ fontSize: 12, color: tokens.faint, flex: 'none', whiteSpace: 'nowrap' }}>
              {formatDateTime(c.createdAt)}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}

/**
 * Dải cảnh báo đầu màn chi tiết trang — spec SC-19 §4 Y3.
 *
 * Chỉ hiện thay đổi của LẦN QUÉT MỚI NHẤT. Không có thay đổi nghiêm trọng thì
 * không tô đỏ: một dải đỏ cho việc "đổi tiêu đề" làm người dùng quen với màu đỏ và
 * bỏ qua nó đúng lúc nó có nghĩa thật.
 */
export function ChangeBanner({ changes }: { changes: readonly PageChangeRow[] }) {
  if (changes.length === 0) return null;
  const nang = changes.filter((c) => c.severity === 'nghiem-trong');

  return (
    <Box
      sx={{
        border: `1px solid ${nang.length > 0 ? '#EFCFD5' : tokens.line}`,
        borderRadius: '10px',
        overflow: 'hidden',
        mb: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: 600,
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          color: nang.length > 0 ? tokens.roseText : tokens.muted,
          bgcolor: nang.length > 0 ? tokens.roseBg : tokens.rowAlt,
          px: 1.5,
          py: 0.9,
          borderBottom: `1px solid ${tokens.line2}`,
        }}
      >
        {/* Nói cả TỔNG khi hai con số khác nhau: ghi mỗi "3 thay đổi cần xem ngay"
            rồi liệt kê sáu dòng bên dưới làm người đọc tưởng đếm sai. */}
        {nang.length > 0
          ? `${nang.length} thay đổi cần xem ngay${
              changes.length > nang.length ? ` (trong ${changes.length} thay đổi)` : ''
            } so với lần quét trước`
          : `${changes.length} thay đổi so với lần quét trước`}
      </Typography>
      <ChangeList changes={changes} />
    </Box>
  );
}
